import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Hook to extract folder hierarchy from URL
 * 
 * Example URLs:
 * - /v2/drives/d/uuid-drive/uuid1/uuid2 → vaultUuid: 'uuid-drive', folderHierarchy: ['uuid1', 'uuid2']
 * - /v2/drives/uuid1/uuid2/uuid3 → vaultUuid: null, folderHierarchy: ['uuid1', 'uuid2', 'uuid3'] (backward compatibility)
 * 
 * Returns: Object with vaultUuid, folderHierarchy, and related info
 */
export const useFolderHierarchy = () => {
  const location = useLocation();

  const result = useMemo(() => {
    const path = location.pathname;
    
    // Remove the /v2/drives/ prefix
    const withoutPrefix = path.replace(/^\/v2\/drives\/?/, '');
    
    // If in my-workspace or shared-with-me, there's no hierarchy
    if (withoutPrefix === 'my-workspace' || withoutPrefix === 'shared-with-me' || withoutPrefix === '') {
      return {
        vaultUuid: null,
        folderHierarchy: [],
        currentFolderId: null,
        parentFolderId: null,
        depth: 0,
        isDriveContext: false
      };
    }
    
    // Split by '/' to get all parts
    const parts = withoutPrefix.split('/').filter(Boolean);
    
    // Check if URL starts with 'd/' (drive context)
    if (parts[0] === 'd' && parts.length > 1) {
      // Drive context: d/vaultUuid/folder1/folder2...
      const vaultUuid = parts[1];
      const folderHierarchy = parts.slice(2); // Everything after vaultUuid
      
      const currentFolderId = folderHierarchy.length > 0 ? folderHierarchy[folderHierarchy.length - 1] : null;
      const parentFolderId = folderHierarchy.length > 1 ? folderHierarchy[folderHierarchy.length - 2] : null;
      
      return {
        vaultUuid,
        folderHierarchy,
        currentFolderId,
        parentFolderId,
        depth: folderHierarchy.length,
        isDriveContext: true
      };
    } else {
      // Backward compatibility: all parts are folders (no drive context)
      const folderHierarchy = parts;
      const currentFolderId = folderHierarchy.length > 0 ? folderHierarchy[folderHierarchy.length - 1] : null;
      const parentFolderId = folderHierarchy.length > 1 ? folderHierarchy[folderHierarchy.length - 2] : null;
      
      return {
        vaultUuid: null,
        folderHierarchy,
        currentFolderId,
        parentFolderId,
        depth: folderHierarchy.length,
        isDriveContext: false
      };
    }
  }, [location.pathname]);

  return result;
};

